<template>
  <teleport to="body">
    <div v-if="visible === true" class="modal-scroll-lock absolute left-0 top-0 w-[100vw] h-[100vh] z-[2147483647] flex items-center justify-center bg-black bg-opacity-60 overflow-hidden">
      <div :dir="isRtl === true ? 'rtl' : 'ltr'" class="bg-white dark:bg-gray-800 rounded-lg p-6 w-11/12 max-w-lg max-h-[90vh] overflow-y-auto">
        <h3 class="text-lg font-semibold mb-1">{{ t(ns + '.selectNumber', mode === 'phone' ? 'Select Phone Number' : 'Select WhatsApp Number') }}</h3>
        <p class="text-xs text-base-content opacity-70 mb-3">{{ t(ns + '.pickHint', mode === 'phone' ? 'Choose a phone number to connect to this agent' : 'Choose a WhatsApp number to connect to this agent') }}</p>
        <div v-if="loading === true" class="py-4 flex items-center">
          <span class="loading loading-spinner loading-sm mr-2"></span>
          <span>{{ t(ns + '.loading', 'Loading available numbers...') }}</span>
        </div>
        <div v-else>
          <div v-if="normalizedItems.length === 0" class="py-2">
            {{ t(ns + '.noneAvailable', mode === 'phone' ? 'No available phone numbers to bind. Create one in Communication Sources.' : 'No available WhatsApp numbers to bind. Create one in Communication Sources.') }}
          </div>
          <div v-else class="py-1 space-y-1 max-h-72 overflow-y-auto">
            <div
              v-for="item in normalizedItems"
              :key="item.sourceId !== undefined && item.sourceId !== null ? item.sourceId : (item.sourceid !== undefined && item.sourceid !== null ? item.sourceid : (item.id !== undefined && item.id !== null ? item.id : item.number))"
              class="card-as-row mx-1 rounded-md cursor-pointer transition-all duration-150 ease-in-out"
              :class="{
                'bg-primary text-primary-content': getItemKey(item) === selectedId,
                'hover:bg-base-200 dark:hover:bg-gray-700': getItemKey(item) !== selectedId
              }"
              @click="selectItem(getItemKey(item))"
            >
              <div class="p-3">
                <div class="flex items-center" :class="isRtl === true ? 'flex-row-reverse' : 'flex-row'">
                  <span class="inline-flex justify-center items-center w-9 h-9 rounded-lg" :class="mode === 'phone' ? 'bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-100' : 'bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-100'">
                    <Icon :icon="mode === 'phone' ? 'tabler:phone' : 'tabler:brand-whatsapp'" class="w-5 h-5" />
                  </span>
                  <div class="flex-grow overflow-hidden px-3" :class="isRtl === true ? 'text-right' : 'text-left'">
                    <div class="text-sm font-semibold truncate" :class="{ 'text-primary-content': item.sourceId === selectedId }">{{ item.display }}</div>
                    <div class="text-xs mt-0.5" :class="item.sourceId === selectedId ? 'text-primary-content opacity-80' : 'text-base-content opacity-70'">{{ item.number }}</div>
                  </div>
                  <input type="checkbox" class="checkbox checkbox-primary" :checked="getItemKey(item) === selectedId" @change="onToggle(getItemKey(item), $event.target.checked)" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-action mt-4 flex justify-end">
          <button class="btn" @click="onClose">{{ t(ns + '.cancel', 'Cancel') }}</button>
          <button class="btn" @click="onConfirm" style="margin-inline-start:8px">{{ t(ns + '.activate', 'Activate') }}</button>
        </div>
      </div>
    </div>
  </teleport>
  </template>
  <script setup>
  import { ref, computed, watch } from 'vue';
  import { Icon } from '@iconify/vue/dist/offline';
  import { useI18n } from '@/i18n';
  const props = defineProps({
    visible: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    currentId: { type: [Number, String], default: null },
    mode: { type: String, default: 'whatsapp' }
  });
  const emit = defineEmits(['close','confirm']);
  const selectedId = ref(null);
  const { t, currentLanguage } = useI18n();
  const mode = computed(() => (props.mode === 'phone' ? 'phone' : 'whatsapp'));
  const ns = computed(() => (mode.value === 'phone' ? 'deploymentChannels.phone' : 'deploymentChannels.whatsapp'));
  const isRtl = computed(() => ['he','ar'].includes(currentLanguage.value) === true ? true : false);
  const normalizedItems = computed(() => Array.isArray(props.items) === true ? props.items : []);
  const isValidSelection = computed(() => {
    const raw = selectedId.value;
    const str = String(raw !== undefined && raw !== null ? raw : '').trim();
    return str.length > 0;
  });
  const selectedItem = computed(() => {
    const key = String(selectedId.value !== undefined && selectedId.value !== null ? selectedId.value : '').trim();
    if (key.length === 0) { return null; }
    for (const it of normalizedItems.value) {
      const k = getItemKey(it);
      if (k === key) { return it; }
    }
    return null;
  });
  watch(() => props.visible, v => {
    if (v === true) {
      if (props.currentId !== null && props.currentId !== undefined && String(props.currentId).length > 0) {
        selectedId.value = String(props.currentId);
      } else {
        selectedId.value = null;
      }
    }
  });
  watch(() => props.items, (items) => {
    if (props.visible === true) {
      if (props.currentId !== null && props.currentId !== undefined && String(props.currentId).length > 0) {
        selectedId.value = String(props.currentId);
      }
    }
  });

  function onClose(){ emit('close'); }
  function onConfirm(){
    const raw = String(selectedId.value !== undefined && selectedId.value !== null ? selectedId.value : '').trim();
    const parsed = parseInt(raw, 10);
    const item = selectedItem.value;
    const num = item !== null && item.number !== undefined && item.number !== null ? String(item.number) : '';
    if (Number.isFinite(parsed) === true) { emit('confirm', { sourceId: parsed, number: num }); return; }
    emit('confirm', { sourceId: null, number: '' });
  }
  function onToggle(id, checked){
    const sid = String(id);
    if (checked === true) { selectedId.value = sid; return; }
    if (String(selectedId.value !== undefined && selectedId.value !== null ? selectedId.value : '') === sid) { selectedId.value = null; }
  }
  function getItemKey(item){
    if (item === undefined || item === null) { return ''; }
    const sid = item.sourceId !== undefined && item.sourceId !== null ? item.sourceId : (item.sourceid !== undefined && item.sourceid !== null ? item.sourceid : (item.id !== undefined && item.id !== null ? item.id : null));
    if (sid !== null) { return String(sid); }
    const n = item.number !== undefined && item.number !== null ? item.number : '';
    return String(n);
  }
  function selectItem(id){ selectedId.value = String(id); }
  </script>
  <style scoped>
  </style>


